import React, { useState } from 'react';
import {
  Box,
  List,
  Typography,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import {
  Dashboard,
  EmojiEvents,
  Star,
  People,
  Analytics,
  Settings,
  Security
} from '@mui/icons-material';
import AchievementManager from './Achievements/AchievementManager';
import BadgeManager from './Achievements/BadgeManager';
import UserAchievementManager from './Achievements/UserAchievementManager';
import AchievementAnalytics from './Achievements/AchievementAnalytics';
import AdminCheats from './Achievements/AdminCheats';
import TitleManager from './Achievements/TitleManager';

const subSidebarWidth = 180;

const AchievementsDashboard = () => {
  const [selectedTab, setSelectedTab] = useState('dashboard');

  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Dashboard },
    { id: 'achievements', label: 'Achievements', icon: EmojiEvents },
    { id: 'badges', label: 'Badges', icon: Star },
    { id: 'titles', label: 'Titles', icon: Settings },
    { id: 'user-achievements', label: 'User Achievements', icon: People },
    { id: 'analytics', label: 'Analytics', icon: Analytics },
    { id: 'cheats', label: 'Admin Cheats', icon: Security },
    { id: 'settings', label: 'Settings', icon: Settings }
  ];

  const renderContent = () => {
    switch (selectedTab) {
      case 'achievements':
        return <AchievementManager />;
      case 'badges':
        return <BadgeManager />;
      case 'titles':
        return <TitleManager />;
      case 'user-achievements':
        return <UserAchievementManager />;
      case 'analytics':
        return <AchievementAnalytics />;
      case 'cheats':
        return <AdminCheats />;
      case 'settings':
        return (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h5">Achievement System Settings</Typography>
            <Typography variant="body1" sx={{ mt: 2 }}>
              Achievement system configuration and settings will be available here.
            </Typography>
          </Paper>
        );
      default:
        return (
          <Paper sx={{ p: 3 }}>
            <Typography variant="h4" gutterBottom>
              Achievements Dashboard
            </Typography>
            <Typography variant="body1">
              Welcome to the HypeCircle Achievements Dashboard. Use the navigation menu to manage achievements, badges, and user progress.
            </Typography>
            
            <Box sx={{ mt: 4 }}>
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2 }}>
                {menuItems.slice(1).map((item) => {
                  const IconComponent = item.icon;
                  return (
                    <Paper 
                      key={item.id}
                      sx={{ p: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                      onClick={() => setSelectedTab(item.id)}
                    >
                      <Box display="flex" alignItems="center" gap={1.5}>
                        <IconComponent color="primary" fontSize="small" />
                        <Box>
                          <Typography variant="subtitle2" fontSize="0.85rem">{item.label}</Typography>
                          <Typography variant="caption" color="text.secondary" fontSize="0.7rem">
                            {item.id === 'achievements' && 'Create and configure achievement definitions'}
                            {item.id === 'badges' && 'Create and manage user status badges'}
                            {item.id === 'titles' && 'Create and configure prestigious user titles'}
                            {item.id === 'user-achievements' && 'Grant or revoke achievements for specific users'}
                            {item.id === 'analytics' && 'View achievement system performance and user engagement'}
                            {item.id === 'cheats' && 'Admin tools for testing and debugging'}
                            {item.id === 'settings' && 'Achievement system configuration'}
                          </Typography>
                        </Box>
                      </Box>
                    </Paper>
                  );
                })}
              </Box>
            </Box>
          </Paper>
        );
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* Achievements Sub-sidebar */}
      <Box
        sx={{
          width: subSidebarWidth,
          flexShrink: 0,
          bgcolor: 'background.paper',
          borderRight: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
            Achievements
          </Typography>
        </Box>
        <List dense>
          {menuItems.map((item) => {
            const IconComponent = item.icon;
            return (
              <ListItem key={item.id} disablePadding>
                <ListItemButton
                  selected={selectedTab === item.id}
                  onClick={() => setSelectedTab(item.id)}
                  sx={{ py: 0.5, px: 1.5 }}
                >
                  <ListItemIcon sx={{ minWidth: 28 }}>
                    <IconComponent fontSize="small" />
                  </ListItemIcon>
                  <ListItemText 
                    primary={item.label} 
                    primaryTypographyProps={{ fontSize: '0.8rem' }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Box>
      
      {/* Content Area */}
      <Box
        sx={{ flexGrow: 1, bgcolor: 'background.default', p: 3, overflow: 'auto' }}
      >
        {renderContent()}
      </Box>
    </Box>
  );
};

export default AchievementsDashboard;